import { useCallback, useMemo } from 'react';
import { useScheduler } from '../SchedulerContext';
import { useStepNavigation } from '../StepNavigationContext';
import { FIRST_STEP, STEP_MAP, TOTAL_STEPS } from '../constants';
import { useStepValidation, type StepValidationResult } from './useStepValidation';

const PROPERTY_STEP = STEP_MAP.property;

/**
 * What the customer must still do on the Property step before its internal pages
 * will let them out — named for the sidebar label they are looking at.
 */
export const PROPERTY_STEP_PROGRESS_BLOCKER =
  'Continue to the More Information step';

export type StepAccess = {
  activeStep: number;
  /**
   * True while a booking or quote send is in flight. Navigation affordances stay
   * visible but must be inert so the customer cannot mutate the payload mid-request.
   * Kept separate from `canAccessStep` so this transient interaction lock never
   * looks like a data problem (and never triggers the activeStep clamp).
   */
  isNavigationLocked: boolean;
  /** May the customer be on this step at all? */
  canAccessStep: (step: number) => boolean;
  getStepValidation: (step: number) => StepValidationResult;
  /**
   * What stops the customer moving forward off `step` right now — its own
   * unfinished data plus anything else the step demands before releasing them.
   * Empty when the step is ready to be left.
   */
  getAdvanceBlockers: (step: number) => string[];
  /**
   * What the customer must finish before `step` opens up — the missing fields of
   * the earliest incomplete step before it. Empty when `step` is already reachable.
   */
  getBlockingFields: (step: number) => string[];
  /**
   * The furthest step the customer is entitled to sit on: the first step whose
   * data is incomplete, or the last step when everything is filled in.
   */
  furthestAllowedStep: number;
};

/**
 * The one place that decides whether a step may be entered.
 *
 * Backward navigation is always allowed — customers legitimately revisit earlier
 * steps to review or edit. Forward navigation requires every preceding step to
 * be complete, which is what stops the top stepper, the summary-bar tiles, a
 * `?step=` deep link or a resumed draft from jumping over the More Information
 * step.
 *
 * Completeness is not the whole story on the Property step. Its internal pages —
 * Recommended Services (the addon upsell) and More Information — hold nothing
 * mandatory for many companies, so a customer whose property details were filled
 * in could leave from Review Details and never see either. Leaving that step
 * therefore also requires having been shown both. The rule is scoped to
 * customers standing *on* the Property step: a resumed draft is placed past it by
 * `useAutoNavigateToStep`, and pulling those customers back through an upsell they
 * saw in an earlier session is not the intent.
 */
export function useStepAccess(): StepAccess {
  const { activeStep } = useStepNavigation();
  const { bookingStatus, isSendingQuote, hasSeenPropertyPages } =
    useScheduler();

  const step1 = useStepValidation(1);
  const step2 = useStepValidation(2);
  const step3 = useStepValidation(3);
  const step4 = useStepValidation(4);
  const step5 = useStepValidation(5);

  const validations = useMemo(
    () => [step1, step2, step3, step4, step5],
    [step1, step2, step3, step4, step5]
  );

  const getStepValidation = useCallback(
    (step: number): StepValidationResult =>
      validations[step - 1] ?? { isValid: true, missingFields: [] },
    [validations]
  );

  /** Index (1-based) of the first step that is not complete. */
  const firstIncompleteStep = useMemo(() => {
    const index = validations.findIndex((validation) => !validation.isValid);
    return index === -1 ? null : index + 1;
  }, [validations]);

  const furthestAllowedStep = firstIncompleteStep ?? TOTAL_STEPS;

  /** True while the Property step still owes the customer pages to walk through. */
  const isHeldOnPropertyStep =
    activeStep === PROPERTY_STEP && !hasSeenPropertyPages;

  const canAccessStep = useCallback(
    (step: number): boolean => {
      if (step < FIRST_STEP || step > TOTAL_STEPS) return false;
      // The entry step and anything at or behind the customer is always open.
      if (step === FIRST_STEP || step <= activeStep) return true;
      // Forward: every preceding step must be complete.
      if (!validations.slice(0, step - 1).every((v) => v.isValid)) return false;
      // …and the Property step must have been walked to its end.
      return !(isHeldOnPropertyStep && step > PROPERTY_STEP);
    },
    [activeStep, isHeldOnPropertyStep, validations]
  );

  const getAdvanceBlockers = useCallback(
    (step: number): string[] => {
      const blockers = [...getStepValidation(step).missingFields];
      if (step === PROPERTY_STEP && isHeldOnPropertyStep) {
        blockers.push(PROPERTY_STEP_PROGRESS_BLOCKER);
      }
      return blockers;
    },
    [getStepValidation, isHeldOnPropertyStep]
  );

  const getBlockingFields = useCallback(
    (step: number): string[] => {
      if (canAccessStep(step)) return [];
      const blocking = validations
        .slice(0, step - 1)
        .find((validation) => !validation.isValid);
      return blocking?.missingFields ?? [];
    },
    [canAccessStep, validations]
  );

  return {
    activeStep,
    isNavigationLocked: bookingStatus !== 'idle' || isSendingQuote,
    canAccessStep,
    getStepValidation,
    getAdvanceBlockers,
    getBlockingFields,
    furthestAllowedStep,
  };
}
